import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePermissions } from "@/lib/permissions";

export default function Payments() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_id: "", amount: 0, type: "advance", method: "upi", reference: "", due_date: "", status: "received" });

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/payments"),
      api.get("/customers", { params: { scope_mode: "tenant" } }),
    ]);
    setItems(p.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const cust = customers.find(c => c.id === f.customer_id);
    if (!cust) return toast.error("Pick a customer");
    await api.post("/payments", { ...f, customer_name: cust.customer_name, amount: +f.amount });
    toast.success("Payment logged"); setOpen(false); load();
    setF({ customer_id: "", amount: 0, type: "advance", method: "upi", reference: "", due_date: "", status: "received" });
  };

  const totalRecv = items.filter(i => i.status === "received").reduce((s, i) => s + i.amount, 0);
  const totalPending = items.filter(i => i.status !== "received").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-muted-foreground mb-2">Finance</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Payments</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {can("payments.create") && (
            <DialogTrigger asChild><Button className="rounded-full" data-testid="add-payment-button"><Plus size={16} className="mr-2" />Log Payment</Button></DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>New Payment</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Select value={f.customer_id} onValueChange={(v) => setF({...f,customer_id:v})}>
                <SelectTrigger data-testid="payment-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">AMOUNT ₹</Label><Input type="number" value={f.amount} onChange={(e) => setF({...f,amount:e.target.value})} data-testid="payment-amount" /></div>
                <div><Label className="text-xs">TYPE</Label>
                  <Select value={f.type} onValueChange={(v) => setF({...f,type:v})}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["advance","balance","full","refund"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">METHOD</Label>
                  <Select value={f.method} onValueChange={(v) => setF({...f,method:v})}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["cash","upi","bank","razorpay","cheque"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">STATUS</Label>
                  <Select value={f.status} onValueChange={(v) => setF({...f,status:v})}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["received","pending","overdue"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs">REFERENCE</Label><Input value={f.reference} onChange={(e) => setF({...f,reference:e.target.value})} /></div>
                <div><Label className="text-xs">DUE DATE</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({...f,due_date:e.target.value})} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-payment-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5"><div className="overline text-muted-foreground">Received</div><div className="font-heading text-3xl font-bold mt-2">₹{totalRecv.toLocaleString("en-IN")}</div></Card>
        <Card className="p-5"><div className="overline text-muted-foreground">Pending/Overdue</div><div className="font-heading text-3xl font-bold mt-2 text-destructive">₹{totalPending.toLocaleString("en-IN")}</div></Card>
        <Card className="p-5"><div className="overline text-muted-foreground">Records</div><div className="font-heading text-3xl font-bold mt-2">{items.length}</div></Card>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr><th className="text-left p-4">Customer</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Type</th><th className="text-left p-4">Method</th><th className="text-left p-4">Status</th><th className="text-left p-4">Date</th></tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-border/40" data-testid={`payment-${p.id}`}>
                <td className="p-4 font-medium">{p.customer_name}</td>
                <td className="p-4 font-heading font-bold">₹{p.amount?.toLocaleString("en-IN")}</td>
                <td className="p-4">{p.type}</td>
                <td className="p-4">{p.method}</td>
                <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${p.status==='received'?'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300':p.status==='overdue'?'bg-destructive/10 text-destructive':'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>{p.status}</span></td>
                <td className="p-4 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-IN")}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No payments yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
